import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import DOMPurify from 'dompurify';
import {useSelector} from 'react-redux';
import {useTheme} from '@mui/material/styles';
import {Alert, Box} from '@mui/material';
import {ROLES} from '../../../../../../constants/roles';
import FORM_AUDIENCE_TYPE from '../../../../../../constants/FormAudienceType';
import useUser from '../../../../../../utility/hooks/useUser';

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const replacePlaceholder = (originalText, placeholder, value, removeWhenMissing) => {
    const hasValue =
        value !== null &&
        value !== undefined &&
        String(value).trim() !== '' &&
        !String(value).trim().startsWith('@');

    const regex = new RegExp(escapeRegExp(placeholder), 'g');

    if (hasValue) {
        return originalText.replace(regex, String(value));
    }

    return removeWhenMissing
        ? originalText.replace(regex, '')
        : originalText;
};

const replaceLegacyPlaceholder = (originalText, placeholder, value, removeWhenMissing) => {
    const hasValue =
        value !== null &&
        value !== undefined &&
        String(value).trim() !== '' &&
        !String(value).trim().startsWith('@');

    if (placeholder === '@Parent') {
        const regex = /@Parent(?![A-Za-z])/g;
        if (hasValue) return originalText.replace(regex, String(value));
        return removeWhenMissing ? originalText.replace(regex, '') : originalText;
    }

    if (placeholder === '@Employee') {
        const regex = /@Employee(?![A-Za-z])/g;
        if (hasValue) return originalText.replace(regex, String(value));
        return removeWhenMissing ? originalText.replace(regex, '') : originalText;
    }

    return originalText;
};

const ALL_PLACEHOLDERS = [
    '@SchoolName',

    '@StudentForename',
    '@StudentFullName',
    '@HisHer',
    '@HeShe',
    '@HimHer',

    '@StudentEmail',
    '@StudentUPN',
    '@StudentDOB',
    '@StudentYearGroup',
    '@StudentForm',
    '@StudentHouse',
    '@StudentBoarderStatus',
    '@StudentAddress',
    '@StudentLegalForename',
    '@StudentLegalFullName',
    '@PreviousSchoolName',
    '@PreviousSchoolEmail',
    '@StudentApplicationStatus',
    '@StudentIntakeGroup',

    '@ParentFullName',
    '@ParentSurname',
    '@ParentEmail',
    '@ParentPhone',
    '@ParentAddress',

    '@EmployeeFullName',
    '@EmployeeSurname',
    '@EmployeeEmail',
    '@EmployeePhone',
    '@EmployeeDOB',
    '@EmployeeAddress',
    '@EmployeeLegalSurname',
    '@EmployeeLegalFullName',

    '@Title',
];

const LEGACY_PLACEHOLDER_REGEX = {
    '@Parent': /@Parent(?![A-Za-z])/,
    '@Employee': /@Employee(?![A-Za-z])/,
};

const STUDENT_PLACEHOLDERS = new Set([
    '@StudentForename',
    '@StudentFullName',
    '@HisHer',
    '@HeShe',
    '@HimHer',
    '@StudentEmail',
    '@StudentUPN',
    '@StudentDOB',
    '@StudentYearGroup',
    '@StudentForm',
    '@StudentHouse',
    '@StudentBoarderStatus',
    '@StudentAddress',
    '@StudentLegalForename',
    '@StudentLegalFullName',
    '@PreviousSchoolName',
    '@PreviousSchoolEmail',
    '@StudentApplicationStatus',
    '@StudentIntakeGroup',
]);

const PARENT_PLACEHOLDERS = new Set([
    '@ParentFullName',
    '@ParentSurname',
    '@ParentEmail',
    '@ParentPhone',
    '@ParentAddress',
    '@Parent', // legacy
    '@Title',
]);

const EMPLOYEE_PLACEHOLDERS = new Set([
    '@EmployeeFullName',
    '@EmployeeSurname',
    '@EmployeeEmail',
    '@EmployeePhone',
    '@EmployeeDOB',
    '@EmployeeAddress',
    '@EmployeeLegalSurname',
    '@EmployeeLegalFullName',
    '@Employee', // legacy
    '@Title',
]);

const SHARED_PLACEHOLDERS = new Set(['@SchoolName']);

const getAllowedPlaceholders = (audience, recipientType) => {
    const allowed = new Set(SHARED_PLACEHOLDERS);

    const addAll = (setToAdd) => setToAdd.forEach(t => allowed.add(t));

    switch (audience) {
        case FORM_AUDIENCE_TYPE.students:
            addAll(STUDENT_PLACEHOLDERS);
            return allowed;

        case FORM_AUDIENCE_TYPE.parentsForChildCompletion:
            addAll(STUDENT_PLACEHOLDERS);
            addAll(PARENT_PLACEHOLDERS);
            return allowed;

        case FORM_AUDIENCE_TYPE.previousSchool:
            addAll(STUDENT_PLACEHOLDERS);
            return allowed;

        case FORM_AUDIENCE_TYPE.employees:
            addAll(EMPLOYEE_PLACEHOLDERS);
            return allowed;

        case FORM_AUDIENCE_TYPE.externalUsers: {
            if (!recipientType) {
                addAll(STUDENT_PLACEHOLDERS);
                addAll(EMPLOYEE_PLACEHOLDERS);
                return allowed;
            }

            if (recipientType === 'student') addAll(STUDENT_PLACEHOLDERS);
            if (recipientType === 'employee') addAll(EMPLOYEE_PLACEHOLDERS);
            return allowed;
        }

        default:
            return allowed;
    }
};

const detectUsedPlaceholders = (text) => {
    if (!text) return [];

    const used = new Set();

    ALL_PLACEHOLDERS.forEach((t) => {
        if (text.includes(t)) used.add(t);
    });

    Object.entries(LEGACY_PLACEHOLDER_REGEX).forEach(([placeholder, regex]) => {
        if (regex.test(text)) used.add(placeholder);
    });

    return Array.from(used);
};

const getAudienceLabel = (audience) => {
    switch (audience) {
        case FORM_AUDIENCE_TYPE.students:
            return 'Students';
        case FORM_AUDIENCE_TYPE.parentsForChildCompletion:
            return 'Parents';
        case FORM_AUDIENCE_TYPE.employees:
            return 'Employees';
        case FORM_AUDIENCE_TYPE.previousSchool:
            return 'Previous school';
        case FORM_AUDIENCE_TYPE.externalUsers:
            return 'External users';
        default:
            return '';
    }
};

const getRecipientTypeLabel = (recipientType) => {
    if (!recipientType) return '';
    const v = String(recipientType).toLowerCase();
    if (v === 'student') return 'Student';
    if (v === 'employee') return 'Employee';
    return recipientType;
};

const RichEditorResultText = ({
                                  id,
                                  description,
                                  placeholderValues,
                                  enableValidation,
                                  error,
                                  audience,
                                  recipientType,
                                  readOnly,
                                  submitMode,
                              }) => {
    const {account} = useSelector(state => state.application);
    const [text, setText] = useState('');
    const theme = useTheme();
    const {user} = useUser();

    const isSchoolUser = Array.isArray(user?.roles) &&
        [ROLES.SCHOOL_ADMIN, ROLES.MEMBER_OF_STAFF, ROLES.SENIOR_MEMBER_OF_STAFF]
            .some(role => user.roles.includes(role));

    const isAudienceUnset = (a) =>
        a === null ||
        a === undefined ||
        a === FORM_AUDIENCE_TYPE.none;

    const invalidPlaceholders = useMemo(() => {
        if (readOnly || submitMode) return [];
        if (!description) return [];
        if (audience === null || audience === undefined) return [];

        const used = detectUsedPlaceholders(description);
        if (!used.length) return [];

        const allowed = getAllowedPlaceholders(audience, recipientType);
        return used.filter(t => !allowed.has(t));
    }, [description, audience, recipientType, readOnly, submitMode]);

    useEffect(() => {
        let resultText = description ?? '';

        const removeWhenMissing = (((!submitMode || readOnly) && isSchoolUser) || ((submitMode || readOnly) && !isSchoolUser)) && submitMode;

        if (placeholderValues) {
            resultText = replacePlaceholder(resultText, '@SchoolName', placeholderValues.schoolName, removeWhenMissing);

            resultText = replacePlaceholder(resultText, '@StudentForename', placeholderValues.studentForename, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@StudentFullName', placeholderValues.studentFullName, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@HisHer', placeholderValues.hisHer, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@HeShe', placeholderValues.heShe, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@HimHer', placeholderValues.himHer, removeWhenMissing);

            resultText = replacePlaceholder(resultText, '@StudentEmail', placeholderValues.studentEmail, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@StudentUPN', placeholderValues.studentUPN, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@StudentDOB', placeholderValues.studentDOB, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@StudentYearGroup', placeholderValues.studentYearGroup, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@StudentForm', placeholderValues.studentForm, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@StudentHouse', placeholderValues.studentHouse, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@StudentBoarderStatus', placeholderValues.studentBoarderStatus, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@StudentAddress', placeholderValues.studentAddress, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@StudentLegalForename', placeholderValues.studentLegalForename, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@StudentLegalFullName', placeholderValues.studentLegalFullName, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@PreviousSchoolName', placeholderValues.previousSchoolName, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@PreviousSchoolEmail', placeholderValues.previousSchoolEmail, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@StudentApplicationStatus', placeholderValues.studentApplicationStatus, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@StudentIntakeGroup', placeholderValues.studentIntakeGroup, removeWhenMissing);

            resultText = replacePlaceholder(resultText, '@ParentFullName', placeholderValues.parentFullName, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@ParentSurname', placeholderValues.parentSurname, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@ParentEmail', placeholderValues.parentEmail, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@ParentPhone', placeholderValues.parentPhone, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@ParentAddress', placeholderValues.parentAddress, removeWhenMissing);
            resultText = replaceLegacyPlaceholder(resultText, '@Parent', placeholderValues.parentFullName ?? placeholderValues.parent, removeWhenMissing);

            resultText = replacePlaceholder(resultText, '@EmployeeFullName', placeholderValues.employeeFullName, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@EmployeeSurname', placeholderValues.employeeSurname, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@EmployeeEmail', placeholderValues.employeeEmail, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@EmployeePhone', placeholderValues.employeePhone, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@EmployeeDOB', placeholderValues.employeeDOB, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@EmployeeAddress', placeholderValues.employeeAddress, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@EmployeeLegalSurname', placeholderValues.employeeLegalSurname, removeWhenMissing);
            resultText = replacePlaceholder(resultText, '@EmployeeLegalFullName', placeholderValues.employeeLegalFullName, removeWhenMissing);
            resultText = replaceLegacyPlaceholder(resultText, '@Employee', placeholderValues.employeeFullName ?? placeholderValues.employee, removeWhenMissing);

            resultText = replacePlaceholder(resultText, '@Title', placeholderValues.title, removeWhenMissing);
        }

        if (removeWhenMissing) {
            ALL_PLACEHOLDERS.forEach((t) => {
                resultText = resultText.replace(new RegExp(escapeRegExp(t), 'g'), '');
            });

            resultText = resultText.replace(/@Parent(?![A-Za-z])/g, '');
            resultText = resultText.replace(/@Employee(?![A-Za-z])/g, '');
        }

        setText(DOMPurify.sanitize(resultText));
    }, [id, account, placeholderValues, readOnly, submitMode, description]);

    return (
        <div
            style={{
                alignItems: 'center',
                paddingBottom: '12px',
            }}
        >
            {!submitMode && !readOnly && !isAudienceUnset(audience) && invalidPlaceholders.length > 0 && (
                <Alert severity="warning" sx={{mb: 1}}>
                    This text contains placeholders that do not match the selected {getAudienceLabel(audience)} audience
                    {audience === FORM_AUDIENCE_TYPE.externalUsers && recipientType
                        ? ` (recipient type: ${getRecipientTypeLabel(recipientType)})`
                        : ''}
                    : {invalidPlaceholders.join(', ')}.
                </Alert>
            )}

            <Box
                sx={{
                    ...theme.typography.body1,
                    color: theme.palette.text.primary,
                }}
            >
                <span dangerouslySetInnerHTML={{__html: text}}/>
                {enableValidation && (
                    <Box
                        component="span"
                        color={error ? theme.palette.error.main : theme.palette.text.primary}
                        style={{
                            alignSelf: 'flex-start',
                            marginLeft: 5
                        }}
                    >*</Box>
                )}
            </Box>
        </div>
    );
};

RichEditorResultText.defaultProps = {
    error: false,
    submitMode: false,
    audience: undefined,
    recipientType: undefined,
};

RichEditorResultText.propTypes = {
    id: PropTypes.string,
    description: PropTypes.string.isRequired,
    readOnly: PropTypes.bool,
    placeholderValues: PropTypes.object.isRequired,
    enableValidation: PropTypes.bool,
    error: PropTypes.bool,
    audience: PropTypes.number,
    recipientType: PropTypes.string,
    submitMode: PropTypes.bool.isRequired,
};

export default RichEditorResultText;
